import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { checkAuth, authenticatedRequest } from '../utils/auth';
import './CreateRestaurant.css';

const CreateRestaurant = () => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    shortDescription: '',
    phone: [''],
    address: '',
    city: '',
    location: {
      coordinates: ['', '']
    },
    activeHours: {
      days: {
        start: '',
        end: ''
      },
      hours: {
        open: '',
        close: ''
      }
    },
    averageRating: '',
    images: [],
    logo: '',
    socialMediaAccounts: {
      instagram: '',
      website: ''
    },
    dailyView: '',
    // Additional fields from restaurant model
    payId: '',
    beaconUUID: [],
    sheba: '',
    restaurantArea: '',
    restaurantDistrict: '',
    persianCatName: '',
    isActive: true,
    innerDelivery: false,
    isOpen: true,
    valueAdded: false,
    facilities: [],
    owner: '',
    subCategory: [],
    totalTables: '',
    occupiedTables: [],
    hasCafe: false,
    slug: '',
    lastFoodUpdate: '',
    payment: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const navigate = useNavigate();

  // Category options for dropdown
  const categoryOptions = [
    'classic',
    'fastfood',
    'cafe',
    'vitamin',
    'foreign',
    'vegetarian',
    'seafood'
  ];

  // Weekday options for dropdown
  const weekDays = [
    { value: 'شنبه', label: 'شنبه' },
    { value: 'یکشنبه', label: 'یکشنبه' },
    { value: 'دوشنبه', label: 'دوشنبه' },
    { value: 'سه‌شنبه', label: 'سه‌شنبه' },
    { value: 'چهارشنبه', label: 'چهارشنبه' },
    { value: 'پنجشنبه', label: 'پنجشنبه' },
    { value: 'جمعه', label: 'جمعه' }
  ];

  // Time options (24-hour format)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push(timeString);
    }
  }

  useEffect(() => {
    const initializeComponent = async () => {
      // Check authentication first
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        setError('برای دسترسی به این بخش باید وارد شوید');
        navigate('/');
        return;
      }
    };
    
    initializeComponent();
  }, [navigate]);

  // Load Leaflet map when showMap is true
  useEffect(() => {
    if (showMap && !mapLoaded) {
      loadLeafletMap();
    }
  }, [showMap, mapLoaded]);

  const loadLeafletMap = () => {
    // Load Leaflet CSS and JS dynamically
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      initializeMap();
      setMapLoaded(true);
    };
    document.head.appendChild(script);
  };

  const initializeMap = () => {
    if (mapRef.current && window.L) {
      // Default center: Tehran, Iran
      const defaultLat = 35.6892;
      const defaultLng = 51.3890;
      
      // Use existing coordinates if available
      const lat = formData.location.coordinates[1] || defaultLat;
      const lng = formData.location.coordinates[0] || defaultLng;

      const map = window.L.map(mapRef.current).setView([lat, lng], 13);

      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      // Add marker if coordinates exist
      if (formData.location.coordinates[0] && formData.location.coordinates[1]) {
        markerRef.current = window.L.marker([lat, lng]).addTo(map);
      }

      // Handle map clicks
      map.on('click', async (e) => {
        const { lat, lng } = e.latlng;
        
        // Remove existing marker
        if (markerRef.current) {
          map.removeLayer(markerRef.current);
        }
        
        // Add new marker
        markerRef.current = window.L.marker([lat, lng]).addTo(map);
        
        // Update coordinates
        setFormData(prev => ({
          ...prev,
          location: {
            coordinates: [lng.toFixed(6), lat.toFixed(6)]
          }
        }));
        
        // Get address from coordinates (reverse geocoding)
        try {
          const geocodingResult = await getAddressFromCoordinates(lat, lng);
          if (geocodingResult) {
            setFormData(prev => ({
              ...prev,
              address: geocodingResult.address,
              city: geocodingResult.city,
              location: {
                coordinates: [lng.toFixed(6), lat.toFixed(6)]
              }
            }));
          }
        } catch (error) {
          console.log('Could not fetch address:', error);
        }
      });
    }
  };



  const getAddressFromCoordinates = async (lat, lng) => {
    try {
      // Using Nominatim (OpenStreetMap) reverse geocoding service
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=fa,en`
      );
      
      if (!response.ok) {
        throw new Error('Geocoding request failed');
      }
      
      const data = await response.json();
      
      if (data && data.display_name) {
        // Format the address for Persian users
        let address = data.display_name;
        
        // If we have structured address components, format them in Persian style
        if (data.address) {
          const components = [];
          
          // Start with most specific location (Persian format)
          // Add house number and street/road
          if (data.address.house_number && (data.address.road || data.address.street)) {
            components.push(`${data.address.road || data.address.street} ${data.address.house_number}`);
          } else if (data.address.road || data.address.street) {
            components.push(data.address.road || data.address.street);
          }
          
          // Add neighbourhood/suburb
          if (data.address.neighbourhood || data.address.suburb) {
            components.push(data.address.neighbourhood || data.address.suburb);
          }
          
          // Add district if available
          if (data.address.city_district || data.address.district) {
            components.push(data.address.city_district || data.address.district);
          }
          
          if (components.length > 0) {
            address = components.join('، ');
          }
        }
        
        // Extract city for separate field
        let city = '';
        if (data.address) {
          city = data.address.city || data.address.town || data.address.village || '';
        }
        
        return {
          address: address,
          city: city
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching address:', error);
      return null;
    }
  };

  const toggleMap = () => {
    setShowMap(!showMap);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('coordinates.')) {
      const index = parseInt(name.split('.')[1]);
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          coordinates: prev.location.coordinates.map((coord, i) => 
            i === index ? value : coord
          )
        }
      }));
    } else if (name.startsWith('activeHours.')) {
      const parts = name.split('.');
      if (parts.length === 3) {
        const [parent, child, grandchild] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [grandchild]: value
            }
          }
        }));
      }
    } else if (name.startsWith('socialMediaAccounts.')) {
      const child = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialMediaAccounts: {
          ...prev.socialMediaAccounts,
          [child]: value
        }
      }));
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...formData.phone];
    newPhones[index] = value;
    setFormData({
      ...formData,
      phone: newPhones
    });
  };

  const addPhone = () => {
    setFormData({
      ...formData,
      phone: [...formData.phone, '']
    });
  };

  const removePhone = (index) => {
    if (formData.phone.length > 1) {
      const newPhones = formData.phone.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        phone: newPhones
      });
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (selectedImages.length + files.length > 4) {
      setError('حداکثر 4 تصویر می‌توانید انتخاب کنید');
      return;
    }

    const validFiles = [];
    const newPreviews = [];

    files.forEach(file => {
      // Check file size (200KB = 200 * 1024 bytes)
      const maxSize = 200 * 1024;
      if (file.size > maxSize) {
        setError('حجم هر تصویر نباید بیشتر از 200 کیلوبایت باشد');
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setError('فقط فایل‌های تصویری (JPG, PNG, GIF) مجاز هستند');
        return;
      }

      validFiles.push(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target.result);
        if (newPreviews.length === validFiles.length) {
          setImagePreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    if (validFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...validFiles]);
      setError(''); // Clear any previous errors
    }
  };

  const removeImage = (index) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      setSelectedLogo(null);
      setLogoPreview(null);
      return;
    }

    // Check file size (200KB = 200 * 1024 bytes)
    const maxSize = 200 * 1024;
    if (file.size > maxSize) {
      setError('حجم لوگو نباید بیشتر از 200 کیلوبایت باشد');
      e.target.value = '';
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('فقط فایل‌های تصویری (JPG, PNG, GIF) مجاز هستند');
      e.target.value = '';
      return;
    }

    setSelectedLogo(file);
    setError(''); // Clear any previous errors

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setSelectedLogo(null);
    setLogoPreview(null);
    // Clear the file input
    const fileInput = document.getElementById('logo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check if we have files to upload
      const hasFiles = selectedImages.length > 0 || selectedLogo;
      
      let response;
      
      if (hasFiles) {
        // Use FormData for file upload
        const formDataToSend = new FormData();
        
        // Add all form fields
        formDataToSend.append('name', formData.name);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('shortDescription', formData.shortDescription);
        formDataToSend.append('phone', JSON.stringify(formData.phone.filter(phone => phone.trim() !== '')));
        formDataToSend.append('address', formData.address);
        formDataToSend.append('city', formData.city);
        formDataToSend.append('location', JSON.stringify({
          type: 'Point',
          coordinates: formData.location.coordinates.map(coord => parseFloat(coord))
        }));
        formDataToSend.append('activeHours', JSON.stringify(formData.activeHours));
        formDataToSend.append('socialMediaAccounts', JSON.stringify(formData.socialMediaAccounts));
        
        // Add additional fields
        formDataToSend.append('payId', formData.payId || '');
        formDataToSend.append('beaconUUID', JSON.stringify(formData.beaconUUID));
        formDataToSend.append('sheba', formData.sheba || '');
        formDataToSend.append('restaurantArea', formData.restaurantArea || '');
        formDataToSend.append('restaurantDistrict', formData.restaurantDistrict || '');
        formDataToSend.append('persianCatName', formData.persianCatName || '');
        formDataToSend.append('isActive', formData.isActive);
        formDataToSend.append('innerDelivery', formData.innerDelivery);
        formDataToSend.append('isOpen', formData.isOpen);
        formDataToSend.append('valueAdded', formData.valueAdded);
        formDataToSend.append('facilities', JSON.stringify(formData.facilities));
        formDataToSend.append('subCategory', JSON.stringify(formData.subCategory));
        formDataToSend.append('totalTables', formData.totalTables || 0);
        formDataToSend.append('occupiedTables', JSON.stringify(formData.occupiedTables));
        formDataToSend.append('hasCafe', formData.hasCafe);
        formDataToSend.append('payment', formData.payment);
        
        if (formData.averageRating) {
          formDataToSend.append('averageRating', formData.averageRating);
        }
        if (formData.dailyView) {
          formDataToSend.append('dailyView', formData.dailyView);
        }
        
        // Add images
        selectedImages.forEach((image, index) => {
          formDataToSend.append('images', image);
        });
        
        // Add logo
        if (selectedLogo) {
          formDataToSend.append('logo', selectedLogo);
        }

        response = await authenticatedRequest('POST', '/restaurants', formDataToSend);
      } else {
        // Use JSON for data without files
        const jsonData = {
          name: formData.name,
          category: formData.category,
          shortDescription: formData.shortDescription,
          phone: formData.phone.filter(phone => phone.trim() !== ''),
          address: formData.address,
          city: formData.city,
          location: {
            type: 'Point',
            coordinates: formData.location.coordinates.map(coord => parseFloat(coord))
          },
          activeHours: formData.activeHours,
          socialMediaAccounts: formData.socialMediaAccounts,
          // Additional fields with default values
          payId: formData.payId || null,
          beaconUUID: formData.beaconUUID,
          sheba: formData.sheba || '',
          restaurantArea: formData.restaurantArea || '',
          restaurantDistrict: formData.restaurantDistrict || '',
          persianCatName: formData.persianCatName || '',
          isActive: formData.isActive,
          innerDelivery: formData.innerDelivery,
          isOpen: formData.isOpen,
          valueAdded: formData.valueAdded,
          facilities: formData.facilities,
          subCategory: formData.subCategory,
          totalTables: formData.totalTables ? parseInt(formData.totalTables) : 0,
          occupiedTables: formData.occupiedTables,
          hasCafe: formData.hasCafe,
          payment: formData.payment,
          images: [],
          logo: ''
        };
        
        if (formData.averageRating) {
          jsonData.averageRating = parseFloat(formData.averageRating);
        }
        if (formData.dailyView) {
          jsonData.dailyView = parseInt(formData.dailyView);
        }

        response = await authenticatedRequest('POST', '/restaurants', jsonData, {
          'Content-Type': 'application/json'
        });
      }

      if (response.data.ok) {
        setSuccess('رستوران با موفقیت ایجاد شد!');
        // Reset form
        setFormData({
          name: '',
          category: '',
          shortDescription: '',
          phone: [''],
          address: '',
          city: '',
          location: {
            coordinates: ['', '']
          },
          activeHours: {
            days: {
              start: '',
              end: ''
            },
            hours: {
              open: '',
              close: ''
            }
          },
          averageRating: '',
          images: [],
          logo: '',
          socialMediaAccounts: {
            instagram: '',
            website: ''
          },
          dailyView: '',
          // Reset additional fields
          payId: '',
          beaconUUID: [],
          sheba: '',
          restaurantArea: '',
          restaurantDistrict: '',
          persianCatName: '',
          isActive: true,
          innerDelivery: false,
          isOpen: true,
          valueAdded: false,
          facilities: [],
          owner: '',
          subCategory: [],
          totalTables: '',
          occupiedTables: [],
          hasCafe: false,
          slug: '',
          lastFoodUpdate: '',
          payment: false
        });
        setSelectedImages([]);
        setImagePreviews([]);
        setSelectedLogo(null);
        setLogoPreview(null);
        setShowMap(false);
      } else {
        setError(response.data.message || 'خطا در ایجاد رستوران');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در اتصال به سرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-restaurant-container">
      <div className="create-restaurant-card">
        <div className="create-restaurant-header">
          <h2>ایجاد رستوران جدید</h2>
          <p>اطلاعات رستوران را وارد کنید</p>
        </div>

        <form onSubmit={handleSubmit} className="create-restaurant-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">نام رستوران:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="نام رستوران"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">دسته‌بندی:</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">انتخاب دسته‌بندی</option>
                {categoryOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="shortDescription">توضیحات کوتاه:</label>
            <textarea
              id="shortDescription"
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              required
              placeholder="توضیحات کوتاه رستوران"
              rows="3"
            />
          </div>

          <div className="phone-numbers">
            <label>شماره تلفن‌ها:</label>
            {formData.phone.map((phone, index) => (
              <div key={index} className="phone-item">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => handlePhoneChange(index, e.target.value)}
                  required
                  placeholder="شماره تلفن"
                />
                {formData.phone.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePhone(index)}
                    className="remove-btn"
                  >
                    حذف
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addPhone}
              className="add-btn"
            >
              + افزودن شماره
            </button>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address">آدرس:</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder="آدرس دقیق رستوران"
                rows="2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">شهر:</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                placeholder="نام شهر"
              />
            </div>
          </div>

          <div className="coordinates-section">
            <label>مختصات جغرافیایی:</label>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="coordinates.0">طول جغرافیایی:</label>
                <input
                  type="number"
                  step="any"
                  name="coordinates.0"
                  value={formData.location.coordinates[0]}
                  onChange={handleChange}
                  required
                  placeholder="51.32238620920256"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coordinates.1">عرض جغرافیایی:</label>
                <input
                  type="number"
                  step="any"
                  name="coordinates.1"
                  value={formData.location.coordinates[1]}
                  onChange={handleChange}
                  required
                  placeholder="35.775162935936656"
                />
              </div>
            </div>
            
            <div className="map-controls">
              <button
                type="button"
                onClick={toggleMap}
                className="map-toggle-btn"
              >
                {showMap ? 'مخفی کردن نقشه' : 'نمایش نقشه برای انتخاب مکان'}
              </button>
            </div>
            
            {showMap && (
              <div className="map-container">
                <div className="map-instructions">
                  <p>روی نقشه کلیک کنید تا مختصات به صورت خودکار پر شود</p>
                </div>
                <div 
                  ref={mapRef} 
                  className="leaflet-map"
                  style={{ height: '400px', width: '100%' }}
                ></div>
              </div>
            )}
          </div>

          <div className="active-hours">
            <label>ساعات کاری:</label>
            <div className="hours-row">
              <div className="form-group">
                <label htmlFor="days.start">روز شروع:</label>
                <select
                  id="days.start"
                  name="activeHours.days.start"
                  value={formData.activeHours.days.start}
                  onChange={handleChange}
                  required
                >
                  <option value="">انتخاب روز شروع</option>
                  {weekDays.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="days.end">روز پایان:</label>
                <select
                  id="days.end"
                  name="activeHours.days.end"
                  value={formData.activeHours.days.end}
                  onChange={handleChange}
                  required
                >
                  <option value="">انتخاب روز پایان</option>
                  {weekDays.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="hours.open">ساعت باز:</label>
                <select
                  id="hours.open"
                  name="activeHours.hours.open"
                  value={formData.activeHours.hours.open}
                  onChange={handleChange}
                  required
                >
                  <option value="">انتخاب ساعت باز</option>
                  {timeOptions.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="hours.close">ساعت بسته:</label>
                <select
                  id="hours.close"
                  name="activeHours.hours.close"
                  value={formData.activeHours.hours.close}
                  onChange={handleChange}
                  required
                >
                  <option value="">انتخاب ساعت بسته</option>
                  {timeOptions.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="averageRating">امتیاز متوسط (اختیاری):</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                id="averageRating"
                name="averageRating"
                value={formData.averageRating}
                onChange={handleChange}
                placeholder="4.0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dailyView">بازدید روزانه (اختیاری):</label>
              <input
                type="number"
                id="dailyView"
                name="dailyView"
                value={formData.dailyView}
                onChange={handleChange}
                placeholder="0"
              />
            </div>
          </div>

          <div className="images-section">
            <label>تصاویر (حداکثر 4 تصویر):</label>
            <div className="image-upload-container">
              <input
                type="file"
                id="images-upload"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="image-input"
                disabled={selectedImages.length >= 4}
              />
              <div className="image-upload-info">
                <p>حداکثر حجم هر تصویر: 200 کیلوبایت</p>
                <p>فرمت‌های مجاز: JPG, PNG, GIF</p>
                <p>تعداد انتخاب شده: {selectedImages.length}/4</p>
              </div>
              
              {imagePreviews.length > 0 && (
                <div className="images-preview-container">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="image-preview-item">
                      <img 
                        src={preview} 
                        alt={`پیش‌نمایش تصویر ${index + 1}`} 
                        className="image-preview"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="remove-image-btn"
                      >
                        حذف
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group logo-upload-section">
            <label htmlFor="logo-upload">لوگو:</label>
            <div className="logo-upload-container">
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoUpload}
                className="logo-input"
              />
              <div className="logo-upload-info">
                <p>حداکثر حجم: 200 کیلوبایت</p>
                <p>فرمت‌های مجاز: JPG, PNG, GIF</p>
              </div>
              
              {logoPreview && (
                <div className="logo-preview-container">
                  <img 
                    src={logoPreview} 
                    alt="پیش‌نمایش لوگو" 
                    className="logo-preview"
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="remove-logo-btn"
                  >
                    حذف لوگو
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="social-media">
            <label>شبکه‌های اجتماعی (اختیاری):</label>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="instagram">اینستاگرام:</label>
                <input
                  type="text"
                  id="instagram"
                  name="socialMediaAccounts.instagram"
                  value={formData.socialMediaAccounts.instagram}
                  onChange={handleChange}
                  placeholder="shandiz.restaurants"
                />
              </div>

              <div className="form-group">
                <label htmlFor="website">وب‌سایت:</label>
                <input
                  type="url"
                  id="website"
                  name="socialMediaAccounts.website"
                  value={formData.socialMediaAccounts.website}
                  onChange={handleChange}
                  placeholder="https://www.shandizjordan.com/"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="cancel-button"
            >
              بازگشت
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'در حال ایجاد...' : 'ایجاد رستوران'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRestaurant;

